/**
 * Gymnastics DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated movement and coordination drills organized into skill levels:
 * 1. Beginner Gymnastics Movement (30 minutes) - Basic coordination and balance
 * 2. Intermediate Gymnastics Movement (45 minutes) - More complex movement patterns
 * 3. Advanced Gymnastics Movement (45 minutes) - Challenging coordination sequences
 * 4. Beginner Balance (30 minutes) - Simple balance poses and positions
 * 5. Intermediate Gymnastics Balance (45 minutes) - More challenging balance work
 * 6. Advanced Balance (45 minutes) - Complex balance combinations
 * 7. Beginner Gymnastics Coordination (30 minutes) - Basic movement combinations
 * 8. Intermediate Gymnastics Coordination (45 minutes) - Advanced movement patterns
 * 9. Advanced Gymnastics Coordination (45 minutes) - Complex movement sequences
 * 10. Complete Gymnastics (60 minutes) - All skills combined
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 10001 (movement-coordination drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'movement-coordination'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Movement', 'Balance', 'Coordination')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'None' or 'Soft mat')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * Gymnastics DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills)
 * - Equipment limited to optional soft mat
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental movement and coordination skills
 * - Progressive skill development within each drill
 * - Emphasis on proper form and technique
 * - Gymnastics-inspired feel without dangerous skills
 *
 * CREATED: [Date] - Gymnastics drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const movementCoordinationDrills = [
  // BEGINNER MOVEMENT (30 minutes)
  {
    id: 2061,
    name: 'Beginner Gymnastics Movement',
    categories: ['Movement', 'beginner'],
    sports: ['gymnastics'],
    difficulty: 'beginner',
    skill: 'Movement',
    points: 27,
    instructions:
      'Beginner Gymnastics Movement is a comprehensive session to develop your basic movement and coordination skills. This 27-minute session combines fundamental movement patterns, basic coordination, and simple balance exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      {
        time: 72,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Knees and toes point the same way.',
      },
      {
        time: 96,
        text: 'Reminder. Two tracks under your feet. Soft knees. Head over hips.',
      },
      {
        time: 120,
        text: 'Ankles warm. Rock heels to toes slow. Keep balance calm.',
      },

      // 3:00–6:00 Gymnastics shapes: straight, tuck, pike, straddle
      {
        time: 144,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms squeeze by ears, fingers long.',
      },
      {
        time: 168,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Breathe steady.',
      },
      {
        time: 192,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 216, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 240,
        text: 'Pike shape. Legs long in front, toes up, fold at hips. Back stays long.',
      },
      { time: 264, text: 'Hold pike for 3, 2, 1. Sit tall. Breathe out slow.' },
      {
        time: 288,
        text: 'Straddle shape. Legs wide, toes up, sit tall. Hands on the floor.',
      },
      { time: 312, text: 'Hold straddle for 3, 2, 1. Sit tall. Chest calm.' },

      // 6:00–9:00 Core shapes: hollow and arch (superman)
      {
        time: 336,
        text: 'Hollow body. Lie on your back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      {
        time: 360,
        text: 'Hold hollow for 3, 2, 1. Lower with control. Neck long.',
      },
      {
        time: 384,
        text: 'Arch or superman. Lie on your belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 408,
        text: 'Hold superman for 3, 2, 1. Lower slow. Breathe steady.',
      },
      {
        time: 432,
        text: 'See saw. Hollow 2 counts, superman 2 counts, repeat smooth.',
      },
      { time: 456, text: 'Reminder. Small lifts. Tummy snug. Keep neck long.' },

      // 9:00–12:00 Locomotion: bear crawl, crab walk, inchworm
      {
        time: 480,
        text: 'Bear crawl. Hands and feet on the floor, hips low, knees hover. Move opposite hand and foot.',
      },
      {
        time: 504,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 528,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 552,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 576,
        text: 'Crab walk forward 6 steps, then back 6. Hips strong.',
      },
      {
        time: 600,
        text: 'Inchworm. Hands to floor, walk hands out to a plank, walk feet in. Knees soft.',
      },

      // 12:00–15:00 Jumps and sticks
      {
        time: 624,
        text: 'Straight jump. Arms swing back, then up by ears, jump small, land soft and bend. Stick your landing.',
      },
      {
        time: 648,
        text: 'Do 3 straight jumps. Say stick as you freeze for 3, 2, 1.',
      },
      {
        time: 672,
        text: 'Tuck jump. Lift knees toward tummy a little. Land soft, knees over toes.',
      },
      { time: 696, text: 'Do 3 tuck jumps. Stick each landing for 3, 2, 1.' },
      {
        time: 720,
        text: 'Half turn jump. Turn tiny, spot front again. Land quiet.',
      },
      { time: 744, text: 'Do 2 half turn jumps. Arms squeeze by ears.' },

      // 15:00–18:00 Balance: airplane and line walk
      {
        time: 768,
        text: 'Airplane balance. Stand on one foot, arms out to the side, chest calm, eyes forward.',
      },
      {
        time: 792,
        text: 'Hold airplane right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 816,
        text: 'Imaginary balance beam. Draw a line on the floor with your eyes.',
      },
      {
        time: 840,
        text: 'Heel to toe walk 6 steps forward. Arms out. Quiet feet.',
      },
      {
        time: 864,
        text: 'Walk backward 6 steps on the line. Knees soft. Breathe steady.',
      },
      {
        time: 888,
        text: 'Reminder. Tall spine. Head over hips. Toes point forward.',
      },

      // 18:00–21:00 Coordination: march, cross-crawl, clap pattern
      {
        time: 912,
        text: 'March tall. Lift knees hip high, opposite arm swings. Soft feet.',
      },
      {
        time: 936,
        text: 'Cross crawl. Elbow touches opposite knee. Switch sides smooth.',
      },
      { time: 960, text: 'Do 8 cross-crawls. Breathe steady. Tummy snug.' },
      {
        time: 984,
        text: 'Clap pattern. Clap thighs, clap hands, clap overhead, repeat.',
      },
      {
        time: 1008,
        text: 'Do the clap pattern 4 times. Stay tall. Smile a little.',
      },
      {
        time: 1032,
        text: 'Reminder. Knees over toes, two tracks, quiet feet.',
      },

      // 21:00–24:00 Shapes to standing: rock and roll, lunge, lever
      {
        time: 1056,
        text: 'Rock and roll. Sit in tuck, rock back gently, rock up to stand. Keep chin tucked.',
      },
      { time: 1080, text: 'Do 3 rock and rolls. Stand up slow and steady.' },
      {
        time: 1104,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1128,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1152,
        text: 'Lever. From lunge, tip forward like a seesaw, back leg lifts, arms by ears, return to lunge.',
      },
      {
        time: 1176,
        text: 'Do 2 slow levers each side. Neck long. Chest calm.',
      },

      // 24:00–27:00 Flexibility: pike, straddle, shoulder
      {
        time: 1200,
        text: 'Seated pike fold. Legs long, toes up, tip from hips, hands to legs.',
      },
      { time: 1224, text: 'Hold pike for 3, 2, 1. Breathe out slow.' },
      {
        time: 1248,
        text: 'Straddle pancake. Legs wide, tip forward, hands slide ahead.',
      },
      { time: 1272, text: 'Hold straddle fold for 3, 2, 1. Sit tall.' },
      {
        time: 1296,
        text: 'Shoulder stretch. Kneel, hands on floor in front, slide hands out, chest toward floor.',
      },
      { time: 1320, text: 'Hold shoulders for 3, 2, 1. Come up slow.' },

      // 27:00–30:00 Mini routine and cooldown
      {
        time: 1344,
        text: 'Teach mini routine. Straight shape, 2 straight jumps, stick, airplane right, heel toe walk 4 steps.',
      },
      {
        time: 1368,
        text: 'Add. Lunge right to lever and back, then rock and roll to stand and salute.',
      },
      {
        time: 1392,
        text: 'Run the mini routine once slow. Breathe steady. Quiet feet.',
      },
      {
        time: 1416,
        text: 'Run it again with confidence. Tall spine. Knees and toes match.',
      },
      {
        time: 1440,
        text: 'Cooldown. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 1464,
        text: 'Calf stretch. Step one foot back, heel down, knee straight. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1488,
        text: 'Arm circles slow. 4 forward, 4 backward. Shoulders down.',
      },
      {
        time: 1512,
        text: 'Neck gentle. Look right, center, left, center. Keep it tiny.',
      },
      {
        time: 1536,
        text: 'Stand tall. Two tracks under feet. Tummy snug. Quiet feet.',
      },
      {
        time: 1560,
        text: 'Salute. Arms by ears then down. Thank your body for moving today.',
      },
      { time: 1584, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },
      {
        time: 1587,
        text: 'Session complete. Beginner Gymnastics Movement finished. Great work today.',
      },
    ],
    timeTotal: 1590,
    equipment: 'None',
  },

  // INTERMEDIATE MOVEMENT (45 minutes)
  {
    id: 2062,
    name: 'Intermediate Gymnastics Movement',
    categories: ['Movement', 'intermediate'],
    sports: ['gymnastics'],
    difficulty: 'intermediate',
    skill: 'Movement',
    points: 38,
    instructions:
      'Intermediate Gymnastics Movement is a comprehensive session to develop your movement skills with more complex patterns. This 38-minute session combines intermediate movement sequences, coordination challenges, and balance exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      { time: 72, text: 'Knees and toes match. That keeps your knees safe.' },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press down, toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Stay calm and ready.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks under feet. Head over hips. Soft landings today.',
      },

      // 3:00–6:00 Gymnastics shapes review and flow
      {
        time: 168,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms by ears, fingers long.',
      },
      {
        time: 192,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 216,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 240, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 264,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back long.',
      },
      { time: 288, text: 'Hold pike for 3, 2, 1. Sit tall. Breathe steady.' },
      {
        time: 312,
        text: 'Straddle shape. Legs wide, toes up, sit tall, hands on the floor.',
      },
      { time: 336, text: 'Hold straddle for 3, 2, 1. Sit tall. Chest calm.' },

      // 6:00–9:00 Core control: hollow and arch (superman)
      {
        time: 360,
        text: 'Hollow body. Lie on back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      { time: 384, text: 'Hold hollow for 3, 2, 1. Lower slow. Neck long.' },
      {
        time: 408,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 432,
        text: 'Hold superman for 3, 2, 1. Lower with control. Breathe out.',
      },
      {
        time: 456,
        text: 'See saw. Hollow 2 counts, superman 2 counts. Switch smooth.',
      },
      { time: 480, text: 'Reminder. Small lifts. Tummy snug. Shoulders down.' },

      // 9:00–12:00 Plank family: front, side, shoulder taps
      {
        time: 504,
        text: 'Plank means long line. Hands under shoulders, tummy tight, squeeze glutes, heels back.',
      },
      { time: 528, text: 'Hold plank for 3, 2, 1. Breathe steady. Neck long.' },
      {
        time: 552,
        text: 'Shoulder taps. From plank, tap right shoulder with left hand, then switch. Hips quiet.',
      },
      { time: 576, text: 'Do 8 slow taps. Knees and toes match. Tummy snug.' },
      {
        time: 600,
        text: 'Side plank right on forearm. Top arm to the sky. Hips lifted.',
      },
      {
        time: 624,
        text: 'Hold side plank for 3, 2, 1. Switch to left. Breathe smooth.',
      },

      // 12:00–15:00 Locomotion 1: bear crawl and crab walk
      {
        time: 648,
        text: 'Bear crawl. Hands and feet on the floor, knees hover, hips low. Move opposite hand and foot.',
      },
      {
        time: 672,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 696,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 720,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 744,
        text: 'Crab walk forward 6 steps, then back 6 steps. Hips strong.',
      },
      {
        time: 768,
        text: 'Reminder. Tall chest, head over hips, move smooth and quiet.',
      },

      // 15:00–18:00 Locomotion 2: inchworm and plank walkouts
      {
        time: 792,
        text: 'Inchworm. From standing, hands to floor, walk hands to a plank, walk feet in. Knees soft.',
      },
      {
        time: 816,
        text: 'Do 2 inchworms slow. Long line in plank, then walk feet in small steps.',
      },
      {
        time: 840,
        text: 'Plank walkouts. From plank, step right hand out wide, then back in. Then left hand. Hips steady.',
      },
      { time: 864, text: 'Do 4 slow walkouts. Breathe steady. Tummy snug.' },
      {
        time: 888,
        text: 'Pike stretch. Hips to the sky, heels heavy, head between arms.',
      },
      {
        time: 912,
        text: 'Hold pike for 3, 2, 1. Walk hands back and stand tall.',
      },

      // 18:00–21:00 Jump patterns and sticks
      {
        time: 936,
        text: 'Stick landing means freeze your finish. Land soft, bend knees, arms out, hold 3, 2, 1.',
      },
      {
        time: 960,
        text: 'Straight jump. Arms back, swing up by ears, jump small, land and stick.',
      },
      {
        time: 984,
        text: 'Do 3 straight jumps. Say stick as you freeze each time.',
      },
      {
        time: 1008,
        text: 'Tuck jump small. Lift knees a little, chest calm, land soft.',
      },
      { time: 1032, text: 'Do 2 tuck jumps. Knees over toes on landings.' },
      {
        time: 1056,
        text: 'Half turn jump. Turn tiny, spot front, stick your landing.',
      },

      // 21:00–24:00 Balance: airplane to arabesque lean and line walk
      {
        time: 1080,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1104,
        text: 'Hold airplane right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1128,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1152,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1. Stand tall.',
      },
      {
        time: 1176,
        text: 'Heel to toe line. Walk on your imaginary beam, 6 steps forward.',
      },
      {
        time: 1200,
        text: 'Quarter turn on the line. Tiny turn. Then 6 steps back. Quiet feet.',
      },

      // 24:00–27:00 Coordination ladders (line drills)
      {
        time: 1224,
        text: 'Line hops two feet. Hop in and out over a floor line. Knees soft.',
      },
      { time: 1248, text: 'Do 8 in-outs slow. Land quiet. Breathe steady.' },
      {
        time: 1272,
        text: 'Forward quick steps. Right, left, right, left over the line. Arms swing small.',
      },
      {
        time: 1296,
        text: 'Do 12 quick steps. Toes kiss the floor, heels light.',
      },
      {
        time: 1320,
        text: 'Icky shuffle. In, in, out, moving sideways. Knees over toes.',
      },
      { time: 1344, text: 'Do 8 counts right, then 8 counts left. Stay tall.' },

      // 27:00–30:00 Lunge, lever, and T-balance (handstand line prep)
      {
        time: 1368,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1392,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1416,
        text: 'Lever. From lunge, tip forward like a seesaw, arms by ears, back leg lifts, then return to lunge.',
      },
      {
        time: 1440,
        text: 'Do 2 slow levers each side. Hips square. Head between arms.',
      },
      {
        time: 1464,
        text: 'T-balance. Tip to a letter T, back leg long, arms by ears, then stand tall.',
      },
      {
        time: 1488,
        text: 'Hold T right for 3, 2, 1. Then T left for 3, 2, 1. Breathe steady.',
      },

      // 30:00–33:00 Donkey kicks and handstand line hold
      {
        time: 1512,
        text: 'Donkey kick small. Hands down, knees bend, tiny hop from both feet, hips go up a little, land soft.',
      },
      {
        time: 1536,
        text: 'Do 4 tiny donkey kicks. Quiet hands. Elbows straight.',
      },
      {
        time: 1560,
        text: 'Handstand line on the floor. Belly to the floor, arms up, squeeze glutes, ribs down, toes point.',
      },
      { time: 1584, text: 'Hold line for 3, 2, 1. Breathe. Arms press long.' },
      {
        time: 1608,
        text: 'Weight shift. In a short lunge, hands down, lean shoulders over hands, lift one foot an inch, switch.',
      },
      {
        time: 1632,
        text: 'Do 4 slow weight shifts. Tummy snug. Head neutral.',
      },

      // 33:00–36:00 Cartwheel prep over a line (space and safety)
      {
        time: 1656,
        text: 'Cartwheel prep is hand hand foot foot over a line. Make space clear and safe.',
      },
      {
        time: 1680,
        text: 'T shape. Face your line, arms wide like a T, step to a lunge. Front knee over toes.',
      },
      {
        time: 1704,
        text: 'Place first hand on the line, then second hand, then land foot foot on the other side. Small and slow.',
      },
      {
        time: 1728,
        text: 'Walk through the steps two times. Hands strong, eyes on the line.',
      },
      {
        time: 1752,
        text: 'If space is not clear, do side bear steps across the line. Hands and feet move sideways.',
      },
      {
        time: 1776,
        text: 'Reminder. Move smooth and safe. Shoulders over hands. Quiet feet.',
      },

      // 36:00–37:30 Mini combo teach and practice
      {
        time: 1800,
        text: 'Mini combo. Lunge right to lever, T-balance, 2 straight jumps, stick, heel toe walk 4 steps.',
      },
      {
        time: 1824,
        text: 'Add. Inchworm to plank, 4 shoulder taps, stand tall and salute.',
      },
      {
        time: 1848,
        text: 'Run the mini combo once slow. Breathe steady. Knees and toes match.',
      },
      {
        time: 1872,
        text: 'Run it again a little faster. Quiet hands and feet. Chest calm.',
      },

      // 37:30–38:00 Cooldown and finish
      {
        time: 1896,
        text: 'Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 1920,
        text: 'Seated pike fold. Legs long, toes up, tip from hips. Hold for 3, 2, 1.',
      },
      {
        time: 1944,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 1968,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1992,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 2016,
        text: 'Stand tall. Two tracks, knees soft, head over hips. Quiet feet.',
      },
      {
        time: 2040,
        text: 'Posture scan. Tall spine, shoulders down, chin level. Breathe steady.',
      },
      { time: 2064, text: 'You moved with skill and care. Small salute.' },
      { time: 2088, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },
      { time: 2112, text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1.' },
      {
        time: 2136,
        text: 'Reminder. Knees over toes. Two tracks. Shoulders down.',
      },
      { time: 2160, text: 'Arms by ears, then down to sides. Calm body.' },
      { time: 2184, text: 'Stand proud. Thank your body for moving today.' },
      { time: 2208, text: 'Hold still and breathe for 3, 2, 1.' },
      { time: 2232, text: 'Session complete hold. Quiet feet for 3, 2, 1.' },
      {
        time: 2237,
        text: 'Session complete. Intermediate Gymnastics Movement finished. Great work today.',
      },
    ],
    timeTotal: 2237,
    equipment: 'None',
  },
  // ADVANCED MOVEMENT (45 minutes)
  {
    id: 2063,
    name: 'Advanced Gymnastics Movement',
    categories: ['Movement', 'advanced'],
    sports: ['gymnastics'],
    difficulty: 'advanced',
    skill: 'Movement',
    points: 38,
    instructions:
      'Advanced Gymnastics Movement is a comprehensive session to develop your movement skills with challenging patterns. This 38-minute session combines advanced movement sequences, complex coordination challenges, and sophisticated balance exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      {
        time: 72,
        text: 'Turn your toes to match your knees. That keeps your knees safe.',
      },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press the floor, toes light and long.',
      },
      {
        time: 120,
        text: 'Breathe in for 3, 2, 1. Breathe out for 3, 2, 1. Stay calm and ready.',
      },

      // 3:00–6:00 Gymnastics shapes: straight, tuck, pike, straddle
      {
        time: 144,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms squeeze by ears, fingers long.',
      },
      {
        time: 168,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 192,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 216, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 240,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back stays long.',
      },
      {
        time: 264,
        text: 'Straddle shape. Legs wide, toes up, sit tall, hands on the floor. Breathe steady.',
      },

      // 6:00–9:00 Core control: hollow and arch (superman)
      {
        time: 288,
        text: 'Hollow body. Lie on your back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      {
        time: 312,
        text: 'Hold hollow for 3, 2, 1. Lower with control. Neck long.',
      },
      {
        time: 336,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 360,
        text: 'Hold superman for 3, 2, 1. Lower slow. Breathe out.',
      },
      {
        time: 384,
        text: 'See saw. Hollow 2 counts, superman 2 counts. Switch smooth.',
      },
      { time: 408, text: 'Reminder. Small lifts, tummy snug, shoulders down.' },

      // 9:00–12:00 Plank family: front, side, shoulder taps
      {
        time: 432,
        text: 'Plank means long line. Hands under shoulders, tummy snug, squeeze glutes, heels back.',
      },
      { time: 456, text: 'Hold plank for 3, 2, 1. Breathe steady. Neck long.' },
      {
        time: 480,
        text: 'Shoulder taps. From plank, tap right shoulder with left hand, then switch. Hips quiet.',
      },
      { time: 504, text: 'Do 8 slow taps. Knees and toes match. Tummy tight.' },
      {
        time: 528,
        text: 'Side plank right on your forearm. Top arm to the sky. Hips lifted.',
      },
      {
        time: 552,
        text: 'Hold side plank for 3, 2, 1. Switch to left. Breathe smooth.',
      },

      // 12:00–15:00 Locomotion 1: bear crawl and crab walk
      {
        time: 576,
        text: 'Bear crawl. Hands and feet on the floor, knees hover, hips low. Move opposite hand and foot.',
      },
      {
        time: 600,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 624,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 648,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 672,
        text: 'Crab walk forward 6 steps, then back 6 steps. Hips strong.',
      },
      {
        time: 696,
        text: 'Reminder. Tall chest, head over hips, move smooth and quiet.',
      },

      // 15:00–18:00 Locomotion 2: inchworm and plank walkouts
      {
        time: 720,
        text: 'Inchworm. From standing, hands to floor, walk hands to a plank, walk feet in. Knees soft.',
      },
      {
        time: 744,
        text: 'Do 2 inchworms slow. Long line in plank, then walk feet in small steps.',
      },
      {
        time: 768,
        text: 'Plank walkouts. From plank, step right hand out wide, then back in. Then left hand. Hips steady.',
      },
      { time: 792, text: 'Do 4 slow walkouts. Breathe steady. Tummy snug.' },
      {
        time: 816,
        text: 'Pike stretch. Hips to the sky, heels heavy, head between arms.',
      },
      {
        time: 840,
        text: 'Hold pike for 3, 2, 1. Walk hands back and stand tall.',
      },

      // 18:00–21:00 Power jumps and stick landings
      {
        time: 864,
        text: 'Stick landing means freeze your finish. Land soft, bend knees, arms out, hold 3, 2, 1.',
      },
      {
        time: 888,
        text: 'Straight jump. Arms back, swing up by ears, jump small, land and stick.',
      },
      {
        time: 912,
        text: 'Do 3 straight jumps. Say stick as you freeze each time.',
      },
      {
        time: 936,
        text: 'Star jump small. Arms and legs open like an X, then close and land soft.',
      },
      { time: 960, text: 'Do 2 star jumps. Knees over toes on landings.' },
      {
        time: 984,
        text: 'Half turn jump. Turn tiny, spot front, stick your landing.',
      },

      // 21:00–24:00 Balance advanced: airplane to arabesque lean
      {
        time: 1008,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1032,
        text: 'Hold airplane right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1056,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1080,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1. Stand tall.',
      },
      {
        time: 1104,
        text: 'Heel to toe line. Walk on your imaginary beam, 6 steps forward.',
      },
      {
        time: 1128,
        text: 'Quarter turn on the line. Tiny turn. Then 6 steps back. Quiet feet.',
      },

      // 24:00–27:00 Coordination patterns
      {
        time: 1152,
        text: 'March tall. Lift knees hip high, opposite arm swings. Soft feet.',
      },
      {
        time: 1176,
        text: 'Cross crawl. Elbow to opposite knee, switch sides smooth. Tummy tight.',
      },
      {
        time: 1200,
        text: 'Do 8 cross crawls slow. Breathe steady. Neck long.',
      },
      {
        time: 1224,
        text: 'Clap pattern. Clap thighs, clap hands, clap overhead, then repeat.',
      },
      { time: 1248, text: 'Do the clap pattern 4 times. Keep posture tall.' },
      {
        time: 1272,
        text: 'Reminder. Two tracks, knees and toes match, shoulders down.',
      },

      // 27:00–30:00 Lunge, lever, and T-balance (handstand line prep)
      {
        time: 1296,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1320,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1344,
        text: 'Lever. From lunge, tip forward like a seesaw, arms by ears, back leg lifts, then return to lunge.',
      },
      {
        time: 1368,
        text: 'Do 2 slow levers each side. Hips square. Head between arms.',
      },
      {
        time: 1392,
        text: 'T-balance. Tip to a letter T, back leg long, arms by ears, then stand tall.',
      },
      {
        time: 1416,
        text: 'Hold T right for 3, 2, 1. Then T left for 3, 2, 1. Breathe steady.',
      },

      // 30:00–33:00 Donkey kicks and wall-free handstand line
      {
        time: 1440,
        text: 'Donkey kick small. Hands down, knees bend, tiny hop from both feet, hips go up a little, land soft.',
      },
      {
        time: 1464,
        text: 'Do 4 tiny donkey kicks. Quiet hands. Elbows straight.',
      },
      {
        time: 1488,
        text: 'Handstand line on the floor. Belly to the floor, arms up, squeeze glutes, ribs down, toes point.',
      },
      { time: 1512, text: 'Hold line for 3, 2, 1. Breathe. Arms press long.' },
      {
        time: 1536,
        text: 'Weight shift. In a short lunge, hands down, lean shoulders over hands, lift one foot an inch, switch.',
      },
      {
        time: 1560,
        text: 'Do 4 slow weight shifts. Tummy snug. Head neutral.',
      },

      // 33:00–36:00 Cartwheel line prep, space and safety
      {
        time: 1584,
        text: 'Cartwheel prep is hand hand foot foot over a line. Make space clear and safe.',
      },
      {
        time: 1608,
        text: 'T shape. Face your line, arms wide like a T, step to a lunge. Front knee over toes.',
      },
      {
        time: 1632,
        text: 'Place first hand on the line, then second hand, then land foot foot on the other side. Small and slow.',
      },
      {
        time: 1656,
        text: 'Walk through the steps slow two times. Hands strong, eyes on the line.',
      },
      {
        time: 1680,
        text: 'If space is not clear, do side bear steps across the line. Hands and feet move sideways.',
      },
      {
        time: 1704,
        text: 'Reminder. Move smooth and safe. Shoulders over hands. Quiet feet.',
      },

      // 36:00–39:00 Mini combo teach
      {
        time: 1728,
        text: 'Mini combo. Lunge right to lever, T-balance, step to line, 2 star jumps, stick.',
      },
      {
        time: 1752,
        text: 'Add. Bear forward 4 steps, crab back 4 steps, inchworm to plank, 4 shoulder taps.',
      },
      {
        time: 1776,
        text: 'Run the mini combo slow. Breathe steady. Knees and toes match.',
      },
      {
        time: 1800,
        text: 'Polish. Arms by ears on levers, soft landings on jumps, calm chest.',
      },
      {
        time: 1824,
        text: 'Run it again a little faster. Quiet hands and feet.',
      },
      { time: 1848, text: 'Reminder. Two tracks, head over hips, tummy snug.' },

      // 39:00–42:00 Performance rounds
      {
        time: 1872,
        text: 'Performance round. Do your mini combo once with confidence.',
      },
      {
        time: 1896,
        text: 'Shake out gently. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 1920,
        text: 'Performance round two. Smooth and strong. Stick your landings.',
      },
      { time: 1944, text: 'Hold tall for 3, 2, 1. Small smile. Quiet feet.' },
      {
        time: 1968,
        text: 'Performance round three. Pick your best side for lever and T. Keep arms long.',
      },
      {
        time: 1992,
        text: 'Reminder. Knees over toes. Shoulders down. Breathe steady.',
      },

      // 42:00–45:00 Cooldown and celebration
      {
        time: 2016,
        text: 'Seated pike fold. Legs long, toes up, tip from hips, hands to legs. Hold for 3, 2, 1.',
      },
      {
        time: 2040,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 2064,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2088,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 2112,
        text: 'Cat cow. Round your back, then lift your chest. Move slow with breath.',
      },
      {
        time: 2136,
        text: 'Stand tall. Two tracks, knees soft, head over hips.',
      },
      {
        time: 2160,
        text: 'Big inhale for 3, 2, 1. Big exhale for 3, 2, 1. Calm body.',
      },
      {
        time: 2184,
        text: 'Posture scan. Tall spine, shoulders down, chin level. Quiet feet.',
      },
      { time: 2208, text: 'You moved with skill and care. Small salute.' },
      { time: 2232, text: 'Hold tall and calm for 3, 2, 1. Breathe steady.' },
      {
        time: 2236,
        text: 'Session complete. Advanced Gymnastics Movement finished. Great work today.',
      },
    ],
    timeTotal: 2239,
    equipment: 'None',
  },
  // INTERMEDIATE BALANCE (45 minutes)
  {
    id: 2064,
    name: 'Intermediate Gymnastics Balance',
    categories: ['Balance', 'intermediate'],
    sports: ['gymnastics'],
    difficulty: 'intermediate',
    skill: 'Balance',
    points: 36,
    instructions:
      'Intermediate Gymnastics Balance is a comprehensive session to develop your balance skills with more challenging poses. This 36-minute session combines intermediate balance poses, stability challenges, and coordination exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // Intermediate Gymnastics Balance Introduction (0-10 minutes)
      {
        time: 4,
        text: "Ready for intermediate balance? Let's build on your balance skills!",
      },
      { time: 10, text: 'Start in your best standing position.' },
      { time: 16, text: 'Arms at your sides, feet together, head up.' },
      { time: 24, text: 'Perfect starting position!' },
      { time: 30, text: 'Now try intermediate balance poses.' },
      { time: 60, text: 'Stand on your tiptoes and hold for longer.' },
      { time: 90, text: 'Great extended tiptoe balance!' },
      {
        time: 120,
        text: 'Now stand on one foot with your arms out like wings.',
      },
      { time: 150, text: 'Great airplane balance!' },
      { time: 180, text: 'Now try standing on one foot with your arms up.' },
      {
        time: 240,
        text: 'Lift your right foot and raise your arms above your head.',
      },
      { time: 270, text: 'Hold this tree pose for several seconds.' },
      { time: 300, text: 'Great tree pose balance!' },
      {
        time: 360,
        text: 'Now try standing on one foot with your arms in different positions.',
      },
      {
        time: 390,
        text: 'Lift your left foot and move your arms to different positions.',
      },
      { time: 420, text: 'Perfect arm position balance!' },
      { time: 480, text: 'Now try intermediate balance movements.' },
      { time: 510, text: 'Stand on one foot while moving your other leg.' },
      { time: 540, text: 'Great leg movement balance!' },
      { time: 600, text: 'Now try balance with coordination.' },

      // Balance Sequences (10-25 minutes)
      {
        time: 660,
        text: "Great intermediate balance! Now let's try balance sequences.",
      },
      {
        time: 720,
        text: 'Try this balance sequence: stand on right foot, then left foot, then both feet.',
      },
      { time: 750, text: 'Start by standing on your right foot.' },
      { time: 780, text: 'Hold for a few seconds.' },
      { time: 810, text: 'Now switch to standing on your left foot.' },
      { time: 840, text: 'Hold for a few seconds.' },
      { time: 870, text: 'Now stand on both feet.' },
      { time: 900, text: 'Great balance sequence!' },
      { time: 960, text: 'Now try this sequence: tiptoes, heels, one foot.' },
      { time: 990, text: 'Start by standing on your tiptoes.' },
      { time: 1020, text: 'Now stand on your heels.' },
      { time: 1050, text: 'Now stand on one foot.' },
      { time: 1080, text: 'Perfect balance sequence!' },
      { time: 1140, text: 'Now try balance with movement patterns.' },
      {
        time: 1170,
        text: 'Stand on one foot while moving your arms in circles.',
      },
      { time: 1200, text: 'Great movement pattern balance!' },
      { time: 1260, text: 'Now try balance with counting.' },
      { time: 1290, text: 'Stand on one foot and count to twenty.' },
      { time: 1320, text: 'Perfect counting balance!' },
      { time: 1380, text: 'Keep practicing your balance sequences.' },

      // Advanced Balance Coordination (25-45 minutes)
      {
        time: 1440,
        text: "Excellent sequences! Now let's work on advanced balance coordination.",
      },
      {
        time: 1500,
        text: 'Try standing on one foot while touching different body parts.',
      },
      {
        time: 1530,
        text: 'Lift your right foot and touch your nose, then your ears.',
      },
      { time: 1560, text: 'Great body part balance!' },
      {
        time: 1620,
        text: 'Now try standing on one foot while making shapes with your arms.',
      },
      {
        time: 1650,
        text: 'Lift your left foot and make a circle with your arms.',
      },
      { time: 1680, text: 'Perfect shape-making balance!' },
      {
        time: 1740,
        text: 'Now try standing on one foot while looking in different directions.',
      },
      {
        time: 1770,
        text: 'Lift your right foot and look up, then down, then left, then right.',
      },
      { time: 1800, text: 'Great direction-looking balance!' },
      {
        time: 1860,
        text: 'Now try standing on one foot while singing or humming.',
      },
      { time: 1890, text: 'Lift your left foot and sing your favorite song.' },
      { time: 1920, text: 'Perfect singing balance!' },
      {
        time: 1980,
        text: 'Keep practicing your advanced balance coordination.',
      },
      { time: 2010, text: "You're becoming an intermediate balance master!" },
      { time: 2040, text: 'Excellent intermediate balance session!' },
      { time: 2070, text: 'Almost done! Keep practicing perfectly.' },
      { time: 2100, text: "Final stretch! You're amazing!" },
      {
        time: 2130,
        text: "Perfect intermediate balance! You've mastered intermediate balance skills!",
      },
    ],
    timeTotal: 2133,
    equipment: 'None',
  },
  // ADVANCED BALANCE (45 minutes)
  {
    id: 2065,
    name: 'Advanced Balance',
    categories: ['Balance', 'advanced'],
    sports: ['gymnastics'],
    difficulty: 'advanced',
    skill: 'Balance',
    points: 45,
    instructions:
      'Advanced Balance is a comprehensive session to develop your balance skills with challenging poses and combinations. This 45-minute session combines advanced balance poses, complex stability challenges, and sophisticated coordination exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // Advanced Balance Introduction (0-10 minutes)
      {
        time: 4,
        text: "Ready for advanced balance? Let's challenge your balance skills!",
      },
      { time: 10, text: 'Start in your perfect standing position.' },
      { time: 16, text: 'Arms at your sides, feet together, head up high.' },
      { time: 24, text: 'Excellent starting position!' },
      { time: 30, text: 'Now try advanced balance poses.' },
      { time: 60, text: 'Stand on your tiptoes and hold for a long time.' },
      { time: 90, text: 'Great extended tiptoe balance!' },
      {
        time: 120,
        text: 'Now stand on one foot with your arms in a star position.',
      },
      { time: 150, text: 'Great star balance!' },
      {
        time: 180,
        text: 'Now try standing on one foot with your other leg extended.',
      },
      {
        time: 240,
        text: 'Lift your right foot and extend your left leg out to the side.',
      },
      { time: 270, text: 'Hold this extended leg balance.' },
      { time: 300, text: 'Great extended leg balance!' },
      {
        time: 360,
        text: 'Now try standing on one foot with your arms in different positions.',
      },
      {
        time: 390,
        text: 'Lift your left foot and move your arms to multiple positions.',
      },
      { time: 420, text: 'Perfect multi-position balance!' },
      { time: 480, text: 'Now try advanced balance movements.' },
      {
        time: 510,
        text: 'Stand on one foot while moving your other leg in circles.',
      },
      { time: 540, text: 'Great leg circle balance!' },
      { time: 600, text: 'Now try complex balance coordination.' },

      // Complex Balance Sequences (10-25 minutes)
      {
        time: 660,
        text: "Great advanced balance! Now let's try complex balance sequences.",
      },
      {
        time: 720,
        text: 'Try this complex sequence: right foot, left foot, tiptoes, heels, right foot.',
      },
      { time: 750, text: 'Start by standing on your right foot.' },
      { time: 780, text: 'Hold for several seconds.' },
      { time: 810, text: 'Now switch to your left foot.' },
      { time: 840, text: 'Hold for several seconds.' },
      { time: 870, text: 'Now stand on your tiptoes.' },
      { time: 900, text: 'Now stand on your heels.' },
      { time: 930, text: 'Finally, stand on your right foot again.' },
      { time: 960, text: 'Great complex balance sequence!' },
      {
        time: 1020,
        text: 'Now try this sequence: one foot, arms up, one foot, arms down.',
      },
      { time: 1050, text: 'Stand on your right foot with arms up.' },
      { time: 1080, text: 'Now stand on your left foot with arms down.' },
      { time: 1110, text: 'Perfect arm sequence balance!' },
      { time: 1170, text: 'Now try balance with complex movement patterns.' },
      {
        time: 1200,
        text: 'Stand on one foot while moving your arms in figure-eight patterns.',
      },
      { time: 1230, text: 'Great figure-eight balance!' },
      { time: 1290, text: 'Now try balance with counting and movement.' },
      {
        time: 1320,
        text: 'Stand on one foot, count to ten, then switch feet.',
      },
      { time: 1350, text: 'Perfect counting and switching balance!' },
      { time: 1410, text: 'Keep practicing your complex balance sequences.' },

      // Mastery Balance Coordination (25-45 minutes)
      {
        time: 1470,
        text: "Excellent sequences! Now let's work on mastery balance coordination.",
      },
      {
        time: 1530,
        text: 'Try standing on one foot while touching multiple body parts in sequence.',
      },
      {
        time: 1560,
        text: 'Lift your right foot and touch your nose, ears, shoulders, and knees.',
      },
      { time: 1590, text: 'Great multi-touch balance!' },
      {
        time: 1650,
        text: 'Now try standing on one foot while making different shapes with your body.',
      },
      {
        time: 1680,
        text: 'Lift your left foot and make a triangle with your arms.',
      },
      { time: 1710, text: 'Perfect shape-making balance!' },
      {
        time: 1770,
        text: 'Now try standing on one foot while looking and moving in different directions.',
      },
      {
        time: 1800,
        text: 'Lift your right foot, look up and raise your arms, then look down and lower them.',
      },
      { time: 1830, text: 'Great direction and movement balance!' },
      {
        time: 1890,
        text: 'Now try standing on one foot while doing multiple activities.',
      },
      {
        time: 1920,
        text: 'Lift your left foot, count to five, and make circles with your arms.',
      },
      { time: 1950, text: 'Perfect multi-activity balance!' },
      {
        time: 2010,
        text: 'Keep practicing your mastery balance coordination.',
      },
      { time: 2040, text: "You're becoming an advanced balance master!" },
      { time: 2070, text: 'Excellent advanced balance session!' },
      { time: 2100, text: 'Almost done! Keep practicing perfectly.' },
      { time: 2130, text: "Final stretch! You're amazing!" },
      {
        time: 2160,
        text: "Perfect advanced balance! You've mastered advanced balance skills!",
      },
      { time: 2190, text: 'Excellent work! Keep practicing!' },
      { time: 2700, text: 'Perfect advanced balance session!' },
    ],
    timeTotal: 2700,
    equipment: 'None',
  },
  // BEGINNER COORDINATION (30 minutes)
  {
    id: 2066,
    name: 'Beginner Gymnastics Coordination',
    categories: ['Coordination', 'beginner'],
    sports: ['gymnastics'],
    difficulty: 'beginner',
    skill: 'Coordination',
    points: 30,
    instructions:
      'Beginner Gymnastics Coordination is a comprehensive session to develop your basic coordination and movement skills. This 30-minute session combines fundamental coordination exercises, simple movement patterns, and basic rhythm work with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      { time: 72, text: 'Knees and toes match. That keeps your knees safe.' },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press the floor, toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Stay calm and ready.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks under feet. Head over hips. Soft landings today.',
      },

      // 3:00–6:00 Gymnastics shapes: straight, tuck, pike, straddle
      {
        time: 168,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms by ears, fingers long.',
      },
      {
        time: 192,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 216,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 240, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 264,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back long.',
      },
      { time: 288, text: 'Hold pike for 3, 2, 1. Sit tall. Breathe steady.' },
      {
        time: 312,
        text: 'Straddle shape. Legs wide, toes up, sit tall, hands on the floor.',
      },
      { time: 336, text: 'Hold straddle for 3, 2, 1. Sit tall. Chest calm.' },

      // 6:00–9:00 Core control: hollow and arch (superman)
      {
        time: 360,
        text: 'Hollow body. Lie on back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      { time: 384, text: 'Hold hollow for 3, 2, 1. Lower slow. Neck long.' },
      {
        time: 408,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 432,
        text: 'Hold superman for 3, 2, 1. Lower with control. Breathe out.',
      },
      {
        time: 456,
        text: 'See saw. Hollow 2 counts, superman 2 counts. Switch smooth.',
      },
      { time: 480, text: 'Reminder. Small lifts. Tummy snug. Shoulders down.' },

      // 9:00–12:00 Cross-body coordination and rhythm
      {
        time: 504,
        text: 'March tall. Lift knees hip high, opposite arm swings. Soft feet.',
      },
      {
        time: 528,
        text: 'Cross crawl. Touch elbow to opposite knee, then switch. Move smooth.',
      },
      {
        time: 552,
        text: 'Do 8 cross crawls slow. Breathe steady. Tummy snug.',
      },
      {
        time: 576,
        text: 'Skips. Step then hop on the same foot, switch feet. Stay light.',
      },
      { time: 600, text: 'Skip 8 counts forward. Quiet feet. Chest calm.' },
      {
        time: 624,
        text: 'Clap pattern. Clap thighs, clap hands, clap overhead, then repeat.',
      },

      // 12:00–15:00 Locomotion 1: bear crawl and crab walk
      {
        time: 648,
        text: 'Bear crawl. Hands and feet down, knees hover, hips low. Move opposite hand and foot.',
      },
      {
        time: 672,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 696,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 720,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 744,
        text: 'Crab walk forward 6 steps, then back 6 steps. Hips strong.',
      },
      {
        time: 768,
        text: 'Reminder. Tall chest, head over hips, move smooth and quiet.',
      },

      // 15:00–18:00 Locomotion 2: inchworm and plank walkouts
      {
        time: 792,
        text: 'Inchworm. From standing, hands to floor, walk hands to a plank, walk feet in. Knees soft.',
      },
      {
        time: 816,
        text: 'Do 2 inchworms slow. Long line in plank, then walk feet in small steps.',
      },
      {
        time: 840,
        text: 'Plank walkouts. From plank, step right hand out wide, then back in. Then left hand. Hips steady.',
      },
      { time: 864, text: 'Do 4 slow walkouts. Breathe steady. Tummy snug.' },
      {
        time: 888,
        text: 'Pike stretch. Hips to the sky, heels heavy, head between arms.',
      },
      {
        time: 912,
        text: 'Hold pike for 3, 2, 1. Walk hands back and stand tall.',
      },

      // 18:00–21:00 Jump patterns and sticks
      {
        time: 936,
        text: 'Stick landing means freeze your finish. Land soft, bend knees, arms out, hold 3, 2, 1.',
      },
      {
        time: 960,
        text: 'Straight jump. Arms back, swing up by ears, jump small, land and stick.',
      },
      {
        time: 984,
        text: 'Do 3 straight jumps. Say stick as you freeze each time.',
      },
      {
        time: 1008,
        text: 'Tuck jump small. Lift knees a little, chest calm, land soft.',
      },
      { time: 1032, text: 'Do 2 tuck jumps. Knees over toes on landings.' },
      {
        time: 1056,
        text: 'Half turn jump. Turn tiny, spot front, stick your landing.',
      },

      // 21:00–24:00 Balance: airplane to arabesque lean and line walk
      {
        time: 1080,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1104,
        text: 'Hold airplane right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1128,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1152,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1. Stand tall.',
      },
      {
        time: 1176,
        text: 'Heel to toe line. Walk on your imaginary beam, 6 steps forward.',
      },
      {
        time: 1200,
        text: 'Quarter turn on the line. Tiny turn. Then 6 steps back. Quiet feet.',
      },

      // 24:00–27:00 Coordination ladders (line drills)
      {
        time: 1224,
        text: 'Line hops two feet. Hop in and out over a floor line. Knees soft.',
      },
      { time: 1248, text: 'Do 8 in-outs slow. Land quiet. Breathe steady.' },
      {
        time: 1272,
        text: 'Forward quick steps. Right, left, right, left over the line. Arms swing small.',
      },
      {
        time: 1296,
        text: 'Do 12 quick steps. Toes kiss the floor, heels light.',
      },
      {
        time: 1320,
        text: 'Icky shuffle. In, in, out, moving sideways. Knees over toes.',
      },
      { time: 1344, text: 'Do 8 counts right, then 8 counts left. Stay tall.' },

      // 27:00–29:00 Pattern play and cross-body taps
      {
        time: 1368,
        text: 'Opposite toe taps. Touch right hand to left toe, then switch sides.',
      },
      { time: 1392, text: 'Do 16 slow taps. Tummy snug. Neck long.' },
      {
        time: 1416,
        text: 'Pat head, rub tummy switch. Pat your head, rub your tummy, then switch hands.',
      },
      {
        time: 1440,
        text: 'Do the switch 6 times slow. Smile a little. Breathe steady.',
      },

      // 29:00–30:00 Mini routine and cooldown
      {
        time: 1464,
        text: 'Mini routine. Straight shape, 2 straight jumps, stick, airplane right, heel toe walk 4 steps.',
      },
      {
        time: 1488,
        text: 'Add. Lunge right to lever and back, then rock and roll to stand and salute.',
      },
      {
        time: 1512,
        text: 'Run the mini routine once slow. Quiet hands and feet.',
      },
      {
        time: 1536,
        text: 'Cooldown. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 1560,
        text: 'Seated pike fold. Legs long, toes up, tip from hips. Hold for 3, 2, 1.',
      },
      {
        time: 1584,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 1608,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 1632,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 1656,
        text: 'Stand tall. Two tracks, knees soft, head over hips.',
      },
      {
        time: 1680,
        text: 'Big inhale for 3, 2, 1. Big exhale for 3, 2, 1. Calm body.',
      },
      {
        time: 1704,
        text: 'Posture scan. Tall spine, shoulders down, chin level. Quiet feet.',
      },
      { time: 1728, text: 'You moved with care today. Small salute.' },
      { time: 1752, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },
      {
        time: 1755,
        text: 'Session complete. Beginner Gymnastics Coordination finished. Great work today.',
      },
    ],
    timeTotal: 1758,
    equipment: 'None',
  },
  // INTERMEDIATE COORDINATION (45 minutes)
  {
    id: 2067,
    name: 'Intermediate Gymnastics Coordination',
    categories: ['Coordination', 'intermediate'],
    sports: ['gymnastics'],
    difficulty: 'intermediate',
    skill: 'Coordination',
    points: 45,
    instructions:
      'Intermediate Gymnastics Coordination is a comprehensive session to develop your coordination skills with more complex patterns. This 45-minute session combines intermediate coordination exercises, movement sequences, and rhythm challenges with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      { time: 72, text: 'Knees and toes match. That keeps your knees safe.' },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press down, toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Stay calm and ready.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks under feet. Head over hips. Soft landings today.',
      },

      // 3:00–6:00 Gymnastics shapes flow
      {
        time: 168,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms by ears, fingers long.',
      },
      {
        time: 192,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 216,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 240, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 264,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back long.',
      },
      { time: 288, text: 'Hold pike for 3, 2, 1. Sit tall. Breathe steady.' },
      {
        time: 312,
        text: 'Straddle shape. Legs wide, toes up, sit tall, hands on the floor.',
      },
      { time: 336, text: 'Hold straddle for 3, 2, 1. Sit tall. Chest calm.' },

      // 6:00–9:00 Core control: hollow and arch (superman)
      {
        time: 360,
        text: 'Hollow body. Lie on back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      { time: 384, text: 'Hold hollow for 3, 2, 1. Lower slow. Neck long.' },
      {
        time: 408,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 432,
        text: 'Hold superman for 3, 2, 1. Lower with control. Breathe out.',
      },
      {
        time: 456,
        text: 'See saw. Hollow 2 counts, superman 2 counts. Switch smooth.',
      },
      { time: 480, text: 'Reminder. Small lifts. Tummy snug. Shoulders down.' },

      // 9:00–12:00 Plank family: front, side, taps
      {
        time: 504,
        text: 'Plank means long line. Hands under shoulders, tummy tight, squeeze glutes, heels back.',
      },
      { time: 528, text: 'Hold plank for 3, 2, 1. Breathe steady. Neck long.' },
      {
        time: 552,
        text: 'Shoulder taps. From plank, tap right shoulder with left hand, then switch. Hips quiet.',
      },
      { time: 576, text: 'Do 8 slow taps. Knees and toes match. Tummy snug.' },
      {
        time: 600,
        text: 'Side plank right on forearm. Top arm to the sky. Hips lifted.',
      },
      {
        time: 624,
        text: 'Hold side plank for 3, 2, 1. Switch to left. Breathe smooth.',
      },

      // 12:00–15:00 Locomotion 1: bear and crab
      {
        time: 648,
        text: 'Bear crawl. Hands and feet down, knees hover, hips low. Move opposite hand and foot.',
      },
      {
        time: 672,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 696,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 720,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 744,
        text: 'Crab walk forward 6 steps, then back 6 steps. Hips strong.',
      },
      {
        time: 768,
        text: 'Reminder. Tall chest, head over hips, move smooth and quiet.',
      },

      // 15:00–18:00 Footwork ladders on a floor line
      {
        time: 792,
        text: 'Line hops two feet. Hop in and out over a floor line. Knees soft.',
      },
      { time: 816, text: 'Do 8 in-outs slow. Land quiet. Breathe steady.' },
      {
        time: 840,
        text: 'Forward quick steps. Right, left, right, left over the line. Arms swing small.',
      },
      {
        time: 864,
        text: 'Do 12 quick steps. Toes kiss the floor, heels light.',
      },
      {
        time: 888,
        text: 'Icky shuffle. In, in, out, moving sideways. Knees over toes.',
      },
      { time: 912, text: 'Do 8 counts right, then 8 counts left. Stay tall.' },

      // 18:00–21:00 Rhythm and pattern play
      {
        time: 936,
        text: 'Clap pattern. Clap thighs, clap hands, clap overhead, then repeat.',
      },
      { time: 960, text: 'Do the clap pattern 4 times. Breathe steady.' },
      {
        time: 984,
        text: 'Step clap. Step right and clap, step left and clap. Keep a beat.',
      },
      { time: 1008, text: 'Step clap 16 counts. Chest calm. Eyes forward.' },
      {
        time: 1032,
        text: 'March and count. Count 1, 2, 3, 4 while you march tall.',
      },
      {
        time: 1056,
        text: 'Reminder. Two tracks, knees and toes match, shoulders down.',
      },

      // 21:00–24:00 Balance progressions
      {
        time: 1080,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1104,
        text: 'Hold airplane right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1128,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1152,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1. Stand tall.',
      },
      {
        time: 1176,
        text: 'Heel to toe line. Walk 6 steps forward on your imaginary beam.',
      },
      {
        time: 1200,
        text: 'Quarter turn on the line. Tiny turn. Then 6 steps back. Quiet feet.',
      },

      // 24:00–27:00 Hand-eye: safe toss and catch (use a soft sock ball)
      {
        time: 1224,
        text: 'Toss and catch. Toss your soft ball a little, eyes on it, hands ready, catch soft.',
      },
      {
        time: 1248,
        text: 'Toss and clap once, then catch. Bend knees a little.',
      },
      { time: 1272, text: 'Do 6 toss-claps. Breathe steady. Quiet feet.' },
      {
        time: 1296,
        text: 'Right hand only toss and catch. Eyes follow the ball.',
      },
      {
        time: 1320,
        text: 'Left hand only toss and catch. Keep it small and safe.',
      },
      {
        time: 1344,
        text: 'Alternate hands toss. Right toss, left catch, then switch. Move slow.',
      },

      // 27:00–30:00 Agility reaction (self-paced)
      {
        time: 1368,
        text: 'Quick feet on the line. Tiny fast steps forward and back. Knees soft.',
      },
      {
        time: 1392,
        text: 'Freeze and stick. Hold still for 3, 2, 1. Breathe out.',
      },
      {
        time: 1416,
        text: 'Drop to plank, then stand tall. Move smooth, no rush.',
      },
      {
        time: 1440,
        text: 'Repeat quick feet for 8 counts. Then freeze again.',
      },
      {
        time: 1464,
        text: 'Side shuffle right 4 steps, left 4 steps. Quiet feet.',
      },
      {
        time: 1488,
        text: 'Reminder. Head over hips, tummy snug, soft landings.',
      },

      // 30:00–33:00 Lunge, lever, and T-balance (handstand line prep)
      {
        time: 1512,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1536,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1560,
        text: 'Lever. From lunge, tip forward like a seesaw, arms by ears, back leg lifts, then return to lunge.',
      },
      {
        time: 1584,
        text: 'Do 2 slow levers each side. Hips square. Head between arms.',
      },
      {
        time: 1608,
        text: 'T-balance. Tip to a letter T, back leg long, arms by ears, then stand tall.',
      },
      {
        time: 1632,
        text: 'Hold T right for 3, 2, 1. Then T left for 3, 2, 1. Breathe steady.',
      },

      // 33:00–36:00 Donkey kicks and handstand line
      {
        time: 1656,
        text: 'Donkey kick small. Hands down, knees bend, tiny hop from both feet, hips go up a little, land soft.',
      },
      {
        time: 1680,
        text: 'Do 4 tiny donkey kicks. Quiet hands. Elbows straight.',
      },
      {
        time: 1704,
        text: 'Handstand line on the floor. Belly to the floor, arms up, squeeze glutes, ribs down, toes point.',
      },
      { time: 1728, text: 'Hold line for 3, 2, 1. Breathe. Arms press long.' },
      {
        time: 1752,
        text: 'Weight shift. Short lunge, hands down, lean shoulders over hands, lift one foot an inch, switch.',
      },
      {
        time: 1776,
        text: 'Do 4 slow weight shifts. Tummy snug. Head neutral.',
      },

      // 36:00–39:00 Cartwheel prep over a line (space and safety)
      {
        time: 1800,
        text: 'Cartwheel prep is hand hand foot foot over a line. Make space clear and safe.',
      },
      {
        time: 1824,
        text: 'T shape. Face your line, arms wide like a T, step to a lunge. Front knee over toes.',
      },
      {
        time: 1848,
        text: 'Place first hand on the line, then second hand, then land foot foot on the other side. Small and slow.',
      },
      {
        time: 1872,
        text: 'Walk through the steps two times. Hands strong, eyes on the line.',
      },
      {
        time: 1896,
        text: 'If space is not clear, do side bear steps across the line. Hands and feet move sideways.',
      },
      {
        time: 1920,
        text: 'Reminder. Move smooth and safe. Shoulders over hands. Quiet feet.',
      },

      // 39:00–42:00 Combo teach and practice
      {
        time: 1944,
        text: 'Mini combo. Quick feet 4 counts, lunge to lever, T-balance, 2 straight jumps, stick.',
      },
      {
        time: 1968,
        text: 'Add. Inchworm to plank, 4 shoulder taps, stand tall and salute.',
      },
      {
        time: 1992,
        text: 'Run the mini combo once slow. Breathe steady. Knees and toes match.',
      },
      {
        time: 2016,
        text: 'Run it again a little faster. Quiet hands and feet. Chest calm.',
      },
      {
        time: 2040,
        text: 'Polish. Arms by ears on levers, soft landings on jumps, calm breath.',
      },
      { time: 2064, text: 'Reminder. Two tracks, head over hips, tummy snug.' },

      // 42:00–44:00 Performance rounds
      {
        time: 2088,
        text: 'Performance round one. Do your mini combo with confidence.',
      },
      {
        time: 2112,
        text: 'Shake out gently. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 2136,
        text: 'Performance round two. Smooth and strong. Stick your landings.',
      },
      { time: 2160, text: 'Hold tall for 3, 2, 1. Small smile. Quiet feet.' },
      {
        time: 2184,
        text: 'Performance round three. Pick your best side for lever and T. Keep arms long.',
      },
      {
        time: 2208,
        text: 'Reminder. Knees over toes. Shoulders down. Breathe steady.',
      },

      // 44:00–45:00 Cooldown and finish
      {
        time: 2232,
        text: 'Seated pike fold. Legs long, toes up, tip from hips. Hold for 3, 2, 1.',
      },
      {
        time: 2256,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 2280,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2304,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 2328,
        text: 'Stand tall. Two tracks, knees soft, head over hips. Quiet feet.',
      },
      {
        time: 2352,
        text: 'Big inhale for 3, 2, 1. Big exhale for 3, 2, 1. Calm body.',
      },
      {
        time: 2376,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 2400, text: 'You moved with skill and care. Small salute.' },
      { time: 2424, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },
      { time: 2448, text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1.' },
      {
        time: 2472,
        text: 'Reminder. Knees over toes. Two tracks. Shoulders down.',
      },
      { time: 2496, text: 'Arms by ears, then down to sides. Calm body.' },
      { time: 2520, text: 'Stand proud. Thank your body for moving today.' },
      { time: 2544, text: 'Hold still and breathe for 3, 2, 1.' },
      {
        time: 2688,
        text: 'Quiet feet. Tall spine. Chin level. Breathe steady... Session complete. Intermediate Gymnastics Coordination finished. Great work today.',
      },
    ],
    timeTotal: 2693,
    equipment: 'None',
  },
  // ADVANCED COORDINATION (45 minutes)
  {
    id: 2068,
    name: 'Advanced Gymnastics Coordination',
    categories: ['Coordination', 'advanced'],
    sports: ['gymnastics'],
    difficulty: 'advanced',
    skill: 'Coordination',
    points: 40,
    instructions:
      'Advanced Gymnastics Coordination is a comprehensive session to develop your coordination skills with challenging patterns and combinations. This 40-minute session combines advanced coordination exercises, complex movement sequences, and sophisticated rhythm challenges with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–3:00 Arrive, safety, athletic stance
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      { time: 72, text: 'Knees and toes match. That keeps your knees safe.' },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press down, toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Stay calm and ready.',
      },

      // 3:00–6:00 Shape flow quick
      {
        time: 144,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms by ears, fingers long.',
      },
      {
        time: 168,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 192,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg.',
      },
      { time: 216, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 240,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back long.',
      },
      {
        time: 264,
        text: 'Straddle shape. Legs wide, toes up, sit tall. Breathe steady. Hold for 3, 2, 1.',
      },

      // 6:00–9:00 Core control advanced
      {
        time: 288,
        text: 'Hollow body. Lie on back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      { time: 312, text: 'Hold hollow for 3, 2, 1. Lower slow. Neck long.' },
      {
        time: 336,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 360,
        text: 'Hold superman for 3, 2, 1. Lower with control. Breathe out.',
      },
      {
        time: 384,
        text: 'Dish to arch rock. Rock smoothly from hollow to arch and back.',
      },
      {
        time: 408,
        text: 'Reminder. Small rocks. Tummy tight. Shoulders down.',
      },

      // 9:00–12:00 Plank family combos
      {
        time: 432,
        text: 'Front plank. Hands under shoulders, long line from head to heels, tummy snug.',
      },
      {
        time: 456,
        text: 'Shoulder taps slow. Tap right shoulder with left hand, then switch. Hips stay quiet.',
      },
      {
        time: 480,
        text: 'Toe taps. From plank, tap one foot out and in, then switch. Knees over toes.',
      },
      {
        time: 504,
        text: 'Side plank right. Forearm down, top arm to sky. Hips lifted. Hold for 3, 2, 1.',
      },
      {
        time: 528,
        text: 'Side plank left. Breathe steady. Neck long. Hold for 3, 2, 1.',
      },
      {
        time: 552,
        text: 'Reminder. Two tracks, head over hips when you stand. Quiet landings next blocks.',
      },

      // 12:00–15:00 Footwork ladders on a floor line
      {
        time: 576,
        text: 'In in out out. Step both feet in over the line, then both feet out. Knees soft.',
      },
      { time: 600, text: 'Do 8 slow reps. Land quiet. Breathe steady.' },
      {
        time: 624,
        text: 'Icky shuffle. In, in, out moving sideways. Arms swing small.',
      },
      {
        time: 648,
        text: 'Do 8 counts right, then 8 counts left. Toes kiss the floor.',
      },
      {
        time: 672,
        text: 'Forward quick steps. Right, left, right, left over the line, then back.',
      },
      {
        time: 696,
        text: 'Do 12 quick steps. Stay tall. Knees and toes match.',
      },

      // 15:00–18:00 Locomotion advanced
      {
        time: 720,
        text: 'Bear crawl lateral. Hands and feet down, knees hover, move sideways right 6 steps.',
      },
      {
        time: 744,
        text: 'Bear crawl sideways left 6 steps. Hips low. Quiet hands.',
      },
      {
        time: 768,
        text: 'Crab reach. From crab walk, reach one hand to the sky, switch sides.',
      },
      { time: 792, text: 'Do 6 reaches total. Hips strong. Breathe steady.' },
      {
        time: 816,
        text: 'Inchworm with one push up. Walk hands to plank, one push up small, walk feet in.',
      },
      { time: 840, text: 'Do 2 inchworms. Chest calm. Neck long.' },

      // 18:00–21:00 Jump patterns and sticks
      {
        time: 864,
        text: 'Stick landing means freeze your finish. Land soft, bend knees, arms out, hold 3, 2, 1.',
      },
      {
        time: 888,
        text: 'Straight jump. Arms swing up by ears, jump small, land and stick.',
      },
      {
        time: 912,
        text: 'Tuck jump small. Lift knees a little, chest calm, land soft.',
      },
      {
        time: 936,
        text: 'Star jump small. Arms and legs open, then close to land soft.',
      },
      {
        time: 960,
        text: 'Half turn jump. Turn tiny, spot front, stick your landing.',
      },
      { time: 984, text: 'Reminder. Knees over toes. Two tracks. Quiet feet.' },

      // 21:00–24:00 Single-leg balance links
      {
        time: 1008,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1032,
        text: 'Airplane right hold 3, 2, 1. Switch to left hold 3, 2, 1.',
      },
      {
        time: 1056,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1080,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1.',
      },
      {
        time: 1104,
        text: 'Knee drive balance. Lift knee hip high, arms by sides, stand tall.',
      },
      {
        time: 1128,
        text: 'Knee drive right hold 3, 2, 1. Switch left hold 3, 2, 1.',
      },

      // 24:00–27:00 Hand-eye toss and catch (soft sock ball)
      {
        time: 1152,
        text: 'Toss and catch. Toss your soft ball a little, eyes on it, hands ready, catch soft.',
      },
      {
        time: 1176,
        text: 'Toss and clap once, then catch. Bend knees a little.',
      },
      { time: 1200, text: 'Two claps then catch. Make it small and safe.' },
      {
        time: 1224,
        text: 'Right hand only toss and catch 6 times. Eyes follow the ball.',
      },
      {
        time: 1248,
        text: 'Left hand only toss and catch 6 times. Quiet feet.',
      },
      {
        time: 1272,
        text: 'Cross catch. Toss with right, catch with left, then switch. Move smooth.',
      },

      // 27:00–30:00 Agility reaction set
      {
        time: 1296,
        text: 'Quick feet on the line. Tiny fast steps forward and back. Knees soft.',
      },
      {
        time: 1320,
        text: 'Down. Drop to plank, hold for 3, 2, 1, then stand tall.',
      },
      { time: 1344, text: 'Quick feet again. Arms calm. Chest steady.' },
      { time: 1368, text: 'Jump. One straight jump, stick for 3, 2, 1.' },
      {
        time: 1392,
        text: 'Side shuffle right 4 steps, left 4 steps. Quiet feet.',
      },
      {
        time: 1416,
        text: 'Reminder. Head over hips, tummy snug, breathe steady.',
      },

      // 30:00–33:00 Acro prep: lunge, lever, kicks, cartwheel over a line
      {
        time: 1440,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1464,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1488,
        text: 'Lever. From lunge, tip forward like a seesaw, arms by ears, back leg lifts, return to lunge.',
      },
      {
        time: 1512,
        text: 'Do 2 levers each side. Hips square. Head between arms.',
      },
      {
        time: 1536,
        text: 'Hand hand foot foot. Cartwheel prep over a floor line, small and safe.',
      },
      {
        time: 1560,
        text: 'Walk the steps twice. Hands strong, eyes on the line. Quiet feet.',
      },

      // 33:00–36:00 Handstand line and bunny hops
      {
        time: 1584,
        text: 'Handstand line on the floor. Belly to floor, arms up, squeeze glutes, ribs down, toes point.',
      },
      {
        time: 1608,
        text: 'Hold the line for 3, 2, 1. Breathe. Arms press long.',
      },
      {
        time: 1632,
        text: 'Weight shift. From short lunge, hands down, lean shoulders over hands, lift one foot an inch, switch.',
      },
      { time: 1656, text: 'Do 4 slow shifts. Tummy snug. Head neutral.' },
      {
        time: 1680,
        text: 'Bunny hops. Hands down, tiny hop from both feet, hips up a little, land soft.',
      },
      { time: 1704, text: 'Do 6 bunny hops. Elbows straight. Quiet hands.' },

      // 36:00–39:00 Pattern memory combo
      {
        time: 1728,
        text: 'Pattern A. Clap thighs, clap hands, clap overhead, two quick steps forward, stick.',
      },
      { time: 1752, text: 'Do Pattern A once. Breathe steady.' },
      {
        time: 1776,
        text: 'Pattern B. Cross crawl 4, side shuffle right 4, left 4, stick.',
      },
      { time: 1800, text: 'Do Pattern B once. Knees and toes match.' },
      {
        time: 1824,
        text: 'Link A then B without stopping. Calm chest. Quiet feet.',
      },
      {
        time: 1848,
        text: 'Reminder. Two tracks, head over hips, soft landings.',
      },

      // 39:00–42:00 Performance rounds
      {
        time: 1872,
        text: 'Performance round one. Quick feet 4, lunge to lever, 2 bunny hops, cartwheel prep, 1 straight jump stick.',
      },
      {
        time: 1896,
        text: 'Shake out gently. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 1920,
        text: 'Performance round two. Add Pattern A at the start.',
      },
      { time: 1944, text: 'Hold tall for 3, 2, 1. Small smile. Quiet feet.' },
      {
        time: 1968,
        text: 'Performance round three. Add Pattern B at the end. Smooth and strong.',
      },
      {
        time: 1992,
        text: 'Reminder. Knees over toes. Shoulders down. Breathe steady.',
      },

      // 42:00–45:00 Cooldown and finish
      {
        time: 2016,
        text: 'Seated pike fold. Legs long, toes up, tip from hips. Hold for 3, 2, 1.',
      },
      {
        time: 2040,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 2064,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2088,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 2112,
        text: 'Stand tall. Two tracks, knees soft, head over hips. Quiet feet.',
      },
      {
        time: 2136,
        text: 'Big inhale for 3, 2, 1. Big exhale for 3, 2, 1. Calm body.',
      },
      {
        time: 2160,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 2184, text: 'You moved with skill and care. Small salute.' },
      { time: 2208, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },
      { time: 2232, text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1.' },
      {
        time: 2256,
        text: 'Reminder. Knees over toes. Two tracks. Shoulders down.',
      },
      { time: 2280, text: 'Arms by ears, then down to sides. Calm body.' },
      { time: 2304, text: 'Stand proud. Thank your body for moving today.' },
      { time: 2328, text: 'Hold still and breathe for 3, 2, 1.' },
      {
        time: 2332,
        text: 'Session complete. Advanced Gymnastics Coordination finished. Great work today.',
      },
    ],
    timeTotal: 2336,
    equipment: 'None',
  },
  // COMPLETE Gymnastics (60 minutes)
  {
    id: 2069,
    name: 'Complete Gymnastics Movement',
    categories: ['Movement', 'Balance', 'Coordination', 'advanced'],
    sports: ['gymnastics'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 60,
    instructions:
      'Complete Gymnastics Movement is a comprehensive session that combines all your movement, balance, and coordination skills. This 60-minute session integrates advanced movement patterns, complex balance poses, and sophisticated coordination exercises with a gymnastics-inspired feel.',
    timedInstructions: [
      // 0:00–4:00 Arrive, safety, athletic stance, breath
      {
        time: 0,
        text: 'Stand tall. Feet under hips on two tracks, knees soft, head over hips. Breathe in your nose, out your mouth.',
      },
      {
        time: 24,
        text: 'Athletic stance means ready body. Bend knees a little, tummy snug, chest calm, arms by your sides.',
      },
      {
        time: 48,
        text: 'Posture check. Tall spine, shoulders down, chin level, eyes forward. Quiet feet.',
      },
      {
        time: 72,
        text: 'Knees and toes match. That keeps your knees safe. Hips face forward.',
      },
      {
        time: 96,
        text: 'Weight even on both feet. Heels press down, toes light and long.',
      },
      {
        time: 120,
        text: 'Breath pattern. In for 3, 2, 1. Out for 3, 2, 1. Calm and ready.',
      },
      {
        time: 144,
        text: 'Reminder. Two tracks under feet. Head over hips. Soft landings today.',
      },

      // 4:00–10:00 Gymnastics shapes flow: straight, tuck, pike, straddle, hollow, arch
      {
        time: 168,
        text: 'Straight shape. Feet together, legs tight, tummy snug, arms by ears, fingers long.',
      },
      {
        time: 192,
        text: 'Hold straight for 3, 2, 1. Lower arms slow. Shoulders stay down.',
      },
      {
        time: 216,
        text: 'Tuck shape. Crouch small, hug knees, round your back like an egg. Neck relaxed.',
      },
      { time: 240, text: 'Hold tuck for 3, 2, 1. Stand tall. Quiet feet.' },
      {
        time: 264,
        text: 'Pike shape sitting. Legs long, toes up, fold at your hips. Back stays long.',
      },
      { time: 288, text: 'Hold pike for 3, 2, 1. Sit tall. Breathe steady.' },
      {
        time: 312,
        text: 'Straddle shape. Legs wide, toes up, sit tall. Hands on the floor.',
      },
      { time: 336, text: 'Hold straddle for 3, 2, 1. Sit tall. Chest calm.' },
      {
        time: 360,
        text: 'Hollow body. Lie on back. Tummy snug, ribs down. Lift shoulders and heels a little.',
      },
      { time: 384, text: 'Hold hollow for 3, 2, 1. Lower slow. Neck long.' },
      {
        time: 408,
        text: 'Arch or superman. Lie on belly. Squeeze glutes, lift chest and heels small, arms long.',
      },
      {
        time: 432,
        text: 'Hold superman for 3, 2, 1. Lower with control. Breathe out.',
      },

      // 10:00–14:00 Core rocks and plank family
      {
        time: 456,
        text: 'Dish to arch rock. Rock smoothly from hollow to arch and back. Small and steady.',
      },
      {
        time: 480,
        text: 'Do 6 small rocks. Tummy tight. Shoulders down. Breathe steady.',
      },
      {
        time: 504,
        text: 'Plank means long line. Hands under shoulders, tummy snug, squeeze glutes, heels back.',
      },
      { time: 528, text: 'Hold plank for 3, 2, 1. Neck long. Quiet breath.' },
      {
        time: 552,
        text: 'Shoulder taps. Tap right shoulder with left hand, then switch. Hips stay quiet.',
      },
      { time: 576, text: 'Do 8 slow taps. Knees and toes match. Tummy snug.' },
      {
        time: 600,
        text: 'Side plank right on forearm. Top arm to the sky. Hips lifted.',
      },
      {
        time: 624,
        text: 'Hold side plank for 3, 2, 1. Switch to left. Breathe smooth.',
      },

      // 14:00–18:00 Locomotion 1: bear and crab
      {
        time: 648,
        text: 'Bear crawl. Hands and feet down, knees hover, hips low. Move opposite hand and foot.',
      },
      {
        time: 672,
        text: 'Bear crawl forward 6 steps. Quiet hands. Heels light.',
      },
      {
        time: 696,
        text: 'Bear crawl back 6 steps. Keep hips low. Breathe out.',
      },
      {
        time: 720,
        text: 'Crab walk. Sit, hands behind you, lift hips a little, walk hands and feet.',
      },
      {
        time: 744,
        text: 'Crab walk forward 6 steps, then back 6 steps. Hips strong.',
      },
      {
        time: 768,
        text: 'Reminder. Tall chest, head over hips, move smooth and quiet.',
      },

      // 18:00–22:00 Locomotion 2: inchworm, walkouts, pike stretch
      {
        time: 792,
        text: 'Inchworm. From standing, hands to floor, walk hands to a plank, walk feet in. Knees soft.',
      },
      {
        time: 816,
        text: 'Do 2 inchworms slow. Long line in plank, then small steps in.',
      },
      {
        time: 840,
        text: 'Plank walkouts. From plank, step right hand out wide, then back in. Then left hand. Hips steady.',
      },
      { time: 864, text: 'Do 4 walkouts. Breathe steady. Tummy snug.' },
      {
        time: 888,
        text: 'Pike stretch. Hips to the sky, heels heavy, head between arms.',
      },
      {
        time: 912,
        text: 'Hold pike for 3, 2, 1. Walk hands back and stand tall.',
      },

      // 22:00–26:00 Footwork ladder on a floor line
      {
        time: 936,
        text: 'In in out out. Step both feet in over the line, then both feet out. Knees soft.',
      },
      { time: 960, text: 'Do 8 slow reps. Land quiet. Breathe steady.' },
      {
        time: 984,
        text: 'Icky shuffle means in, in, out moving sideways. Arms swing small.',
      },
      {
        time: 1008,
        text: 'Do 8 counts right, then 8 counts left. Toes kiss the floor.',
      },
      {
        time: 1032,
        text: 'Forward quick steps. Right, left, right, left over the line, then back.',
      },
      {
        time: 1056,
        text: 'Do 12 quick steps. Stay tall. Knees and toes match.',
      },

      // 26:00–30:00 Jump patterns and sticks
      {
        time: 1080,
        text: 'Stick landing means freeze your finish. Land soft, bend knees, arms out, hold 3, 2, 1.',
      },
      {
        time: 1104,
        text: 'Straight jump. Arms swing up by ears, jump small, land and stick.',
      },
      {
        time: 1128,
        text: 'Do 3 straight jumps. Say stick as you freeze each time.',
      },
      {
        time: 1152,
        text: 'Tuck jump small. Lift knees a little, chest calm, land soft.',
      },
      { time: 1176, text: 'Do 2 tuck jumps. Knees over toes on landings.' },
      {
        time: 1200,
        text: 'Star jump small. Arms and legs open wide like a star, then close to land soft.',
      },

      // 30:00–34:00 Balance progressions: airplane, arabesque, knee drive
      {
        time: 1224,
        text: 'Airplane balance. Stand on one foot, arms out, chest calm, eyes forward.',
      },
      {
        time: 1248,
        text: 'Airplane right hold 3, 2, 1. Switch to left hold 3, 2, 1.',
      },
      {
        time: 1272,
        text: 'Arabesque lean. Tip forward a little, back leg long, toes point, tummy snug.',
      },
      {
        time: 1296,
        text: 'Lean right for 3, 2, 1. Stand tall. Lean left for 3, 2, 1.',
      },
      {
        time: 1320,
        text: 'Knee drive balance. Lift knee hip high, stand tall, arms calm by sides.',
      },
      {
        time: 1344,
        text: 'Knee drive right hold 3, 2, 1. Switch left hold 3, 2, 1.',
      },

      // 34:00–38:00 Hand-eye toss and catch (soft sock ball)
      {
        time: 1368,
        text: 'Toss and catch. Toss your soft ball a little, eyes on it, hands ready, catch soft.',
      },
      {
        time: 1392,
        text: 'Toss and clap once, then catch. Bend knees a little.',
      },
      { time: 1416, text: 'Two claps then catch. Make it small and safe.' },
      {
        time: 1440,
        text: 'Right hand only toss and catch 6 times. Eyes follow the ball.',
      },
      {
        time: 1464,
        text: 'Left hand only toss and catch 6 times. Quiet feet.',
      },
      {
        time: 1488,
        text: 'Cross catch. Toss with right, catch with left, then switch. Move smooth.',
      },

      // 38:00–42:00 Agility reaction set
      {
        time: 1512,
        text: 'Quick feet on the line. Tiny fast steps forward and back. Knees soft.',
      },
      {
        time: 1536,
        text: 'Freeze and stick. Hold still for 3, 2, 1. Breathe out.',
      },
      {
        time: 1560,
        text: 'Down. Drop to plank, hold for 3, 2, 1, then stand tall.',
      },
      { time: 1584, text: 'Quick feet again. Arms calm. Chest steady.' },
      { time: 1608, text: 'Jump. One straight jump, stick for 3, 2, 1.' },
      {
        time: 1632,
        text: 'Side shuffle right 4 steps, left 4 steps. Quiet feet.',
      },

      // 42:00–46:00 Acro preps: lunge, lever, T-balance
      {
        time: 1656,
        text: 'Lunge shape. One foot forward, back heel up, front knee over toes, arms by ears.',
      },
      {
        time: 1680,
        text: 'Hold lunge right for 3, 2, 1. Switch and hold left for 3, 2, 1.',
      },
      {
        time: 1704,
        text: 'Lever. From lunge, tip forward like a seesaw, arms by ears, back leg lifts, return to lunge.',
      },
      {
        time: 1728,
        text: 'Do 2 levers each side. Hips square. Head between arms.',
      },
      {
        time: 1752,
        text: 'T-balance. Tip to a letter T, back leg long, arms by ears, then stand tall.',
      },
      {
        time: 1776,
        text: 'Hold T right for 3, 2, 1. Then T left for 3, 2, 1. Breathe steady.',
      },

      // 46:00–50:00 Bunny hops and cartwheel prep over a line
      {
        time: 1800,
        text: 'Bunny hops. Hands down, tiny hop from both feet, hips up a little, land soft.',
      },
      { time: 1824, text: 'Do 6 bunny hops. Elbows straight. Quiet hands.' },
      {
        time: 1848,
        text: 'Cartwheel prep is hand hand foot foot over a floor line. Make space clear and safe.',
      },
      {
        time: 1872,
        text: 'T shape. Face the line, arms wide like a T, step to a lunge. Front knee over toes.',
      },
      {
        time: 1896,
        text: 'Place first hand on the line, then second, then land foot foot on the other side. Small and slow.',
      },
      {
        time: 1920,
        text: 'Walk the steps two times. Hands strong, eyes on the line. Quiet feet.',
      },

      // 50:00–56:00 Combo teach, link, and performance rounds
      {
        time: 1944,
        text: 'Pattern A. Clap thighs, clap hands, clap overhead, two quick steps forward, stick.',
      },
      {
        time: 1968,
        text: 'Pattern B. Cross crawl 4, side shuffle right 4, left 4, stick.',
      },
      {
        time: 1992,
        text: 'Link A then B without stopping. Calm chest. Quiet feet.',
      },
      {
        time: 2016,
        text: 'Mini combo. Quick feet 4, lunge to lever, 2 bunny hops, cartwheel prep, straight jump stick.',
      },
      {
        time: 2040,
        text: 'Run the mini combo once slow. Knees and toes match. Breathe steady.',
      },
      {
        time: 2064,
        text: 'Run it again a little faster. Arms by ears on levers, soft landings on jumps.',
      },
      {
        time: 2088,
        text: 'Performance round one. Do your full combo with confidence.',
      },
      {
        time: 2112,
        text: 'Shake out gently. Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      {
        time: 2136,
        text: 'Performance round two. Smooth and strong. Stick your landings.',
      },
      { time: 2160, text: 'Hold tall for 3, 2, 1. Small smile. Quiet feet.' },
      {
        time: 2184,
        text: 'Performance round three. Pick your best side for lever and T. Keep arms long.',
      },
      {
        time: 2208,
        text: 'Reminder. Two tracks, head over hips, tummy snug, breathe steady.',
      },

      // 56:00–60:00 Cooldown and finish
      {
        time: 2232,
        text: 'Seated pike fold. Legs long, toes up, tip from hips. Hold for 3, 2, 1.',
      },
      {
        time: 2256,
        text: 'Straddle fold. Legs wide, walk hands forward. Hold for 3, 2, 1. Sit tall.',
      },
      {
        time: 2280,
        text: 'Figure four right. Ankle on knee, sit tall. Hold for 3, 2, 1. Switch.',
      },
      {
        time: 2304,
        text: 'Shoulder stretch. Kneel, hands out in front, slide hands, chest toward floor. Hold for 3, 2, 1.',
      },
      {
        time: 2328,
        text: 'Cat cow. Round your back, then lift your chest. Move slow with breath.',
      },
      {
        time: 2352,
        text: 'Stand tall. Two tracks, knees soft, head over hips. Quiet feet.',
      },
      {
        time: 2376,
        text: 'Big inhale for 3, 2, 1. Big exhale for 3, 2, 1. Calm body.',
      },
      {
        time: 2400,
        text: 'Posture scan. Tall spine, shoulders down, chin level.',
      },
      { time: 2424, text: 'You moved with strength and care. Small salute.' },
      { time: 2448, text: 'Hold tall and calm for 3, 2, 1. Small smile.' },

      // Gentle spacing to complete 60:00 with micro-reminders
      {
        time: 2520,
        text: 'Reminder. Knees over toes. Two tracks. Shoulders down.',
      },
      { time: 2640, text: 'Arms by ears, then down to sides. Quiet feet.' },
      { time: 2760, text: 'Neck long. Chest calm. Breathe steady.' },
      { time: 2880, text: 'Stand proud. Thank your body for moving today.' },
      {
        time: 3000,
        text: 'Big breath in for 3, 2, 1. Long breath out for 3, 2, 1.',
      },
      { time: 3120, text: 'Head over hips. Soft knees. Calm shoulders.' },
      { time: 3240, text: 'Feet on two tracks. Toes and knees match.' },
      { time: 3360, text: 'Hold tall and calm for 3, 2, 1. Quiet feet.' },
      { time: 3480, text: 'Small smile. You practiced with focus and care.' },
      {
        time: 3483,
        text: 'Session complete. Complete Movement and Coordination finished. Great work today.',
      },
    ],
    timeTotal: 3488,
    equipment: 'None',
  },
];

export default movementCoordinationDrills;
